/**
 * Cash vs bank (account) movement totals for dashboard / audit views.
 */
import { readAccountingBundle } from './accountingStore';
import { readConsultancyBundle } from './consultancyStore';
import { inRange, toDate, type DateRange } from './dateFilter';
import { paymentReceiptTotal } from './portfolioMetrics';
import { resolveCashBankAmounts, type PaymentVia } from './paymentVia';
import type { BooksOpeningState } from './booksOpeningStore';
import {
  readAdminEmiPayments,
  readFinanceRecords,
  readHandLoanRecords,
} from './workspaceStore';

export type PaymentFlowSummary = {
  /** EMI / collections / sales received in cash */
  cashTaken: number;
  /** EMI / collections / sales credited to bank */
  bankCredited: number;
  /** Loan principal + stock buys disbursed in cash */
  cashGiven: number;
  /** Loan principal + stock buys disbursed via bank */
  bankDebited: number;
  /** Operating outflows in cash (expenses + advances + salaries) */
  opexCashGiven: number;
  /** Operating outflows via bank/UPI */
  opexBankDebited: number;
  /** All cash out = loans + opex */
  cashGivenTotal: number;
  /** All bank debit = loans + opex */
  bankDebitedTotal: number;
};

function num(v: unknown): number {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function isBankMethod(v: unknown): boolean {
  const s = String(v ?? '').toLowerCase();
  return s === 'bank' || s === 'upi' || s === 'online';
}

export function isMigratedLoan(data: Record<string, unknown> | undefined): boolean {
  if (!data) return false;
  return (
    String(data.migrated ?? '').toLowerCase() === 'yes' ||
    String(data.originSystem ?? '').toLowerCase() === 'migrated'
  );
}

/** Disbursement posting date for flows — agreement/start for live loans. */
function loanFlowDate(r: { createdAt?: string; data?: Record<string, unknown> }): string {
  const d = r.data || {};
  return String(d.agreementDate || d.startDate || r.createdAt || '');
}

/** Aggregate cash in/out and bank credit/debit for the selected date window. */
export function computePaymentFlows(
  range: DateRange,
  emiPayments: ReturnType<typeof readAdminEmiPayments> = readAdminEmiPayments(),
): PaymentFlowSummary {
  let cashTaken = 0;
  let bankCredited = 0;
  let cashGiven = 0;
  let bankDebited = 0;
  let opexCashGiven = 0;
  let opexBankDebited = 0;

  for (const p of emiPayments) {
    if (!inRange(p.paidAt, range)) continue;
    const amt = paymentReceiptTotal(p);
    if (amt <= 0) continue;
    const split = resolveCashBankAmounts(
      p.paymentVia as PaymentVia | '',
      amt,
      p.cashAmount,
      p.bankAmount
    );
    cashTaken += split.cash;
    bankCredited += split.bank;
  }

  // Migrated reopenings are opening stock, not new cash-out disbursements.
  for (const r of readFinanceRecords()) {
    if (isMigratedLoan(r.data as Record<string, unknown>)) continue;
    if (!inRange(loanFlowDate(r), range)) continue;
    const amt = num(r.data.totalAmount || r.data.amount);
    if (amt <= 0) continue;
    const split = resolveCashBankAmounts(
      r.data.disbursementVia as PaymentVia | '',
      amt,
      r.data.disbursementCashAmount,
      r.data.disbursementBankAmount
    );
    cashGiven += split.cash;
    bankDebited += split.bank;
  }

  for (const r of readHandLoanRecords()) {
    if (isMigratedLoan(r.data as Record<string, unknown>)) continue;
    if (!inRange(loanFlowDate(r), range)) continue;
    const amt = num(r.data.loanAmount);
    if (amt <= 0) continue;
    const split = resolveCashBankAmounts(
      r.data.disbursementVia as PaymentVia | '',
      amt,
      r.data.disbursementCashAmount,
      r.data.disbursementBankAmount
    );
    cashGiven += split.cash;
    bankDebited += split.bank;
  }

  // Consultancy stock buys (money out) and sales (money in).
  try {
    const c = readConsultancyBundle();
    for (const v of c.vehicles) {
      if (!inRange(String(v.boughtAt || ''), range)) continue;
      const amt = Number(v.price) || 0;
      if (amt <= 0) continue;
      const split = resolveCashBankAmounts(
        v.paymentVia as PaymentVia | '',
        amt,
        v.cashAmount,
        v.bankAmount
      );
      cashGiven += split.cash;
      bankDebited += split.bank;
    }
    for (const s of c.sales) {
      if (!inRange(String(s.recordedAt || ''), range)) continue;
      const amt = Math.max(0, parseFloat(String(s.sellingPrice ?? '')) || 0);
      if (amt <= 0) continue;
      const split = resolveCashBankAmounts(
        s.paymentVia as PaymentVia | '',
        amt,
        s.cashAmount as number | undefined,
        s.bankAmount as number | undefined
      );
      cashTaken += split.cash;
      bankCredited += split.bank;
    }
  } catch {
    /* consultancy store optional */
  }

  const acct = readAccountingBundle();
  for (const e of acct.expenses) {
    if (!inRange(e.expenseDate, range)) continue;
    const amt = Number(e.amount) || 0;
    if (amt <= 0) continue;
    if (isBankMethod(e.paymentMethod)) opexBankDebited += amt;
    else opexCashGiven += amt;
  }
  for (const a of acct.advances) {
    if (!inRange(a.advanceDate, range)) continue;
    const amt = Number(a.amount) || 0;
    if (amt <= 0) continue;
    if (isBankMethod(a.paymentMethod)) opexBankDebited += amt;
    else opexCashGiven += amt;
  }
  for (const s of acct.salaryPayments) {
    if (!inRange(s.paymentDate, range)) continue;
    const amt = Number(s.netPay) || 0;
    if (amt <= 0) continue;
    if (isBankMethod(s.paymentMethod)) opexBankDebited += amt;
    else opexCashGiven += amt;
  }

  return {
    cashTaken: Math.round(cashTaken),
    bankCredited: Math.round(bankCredited),
    cashGiven: Math.round(cashGiven),
    bankDebited: Math.round(bankDebited),
    opexCashGiven: Math.round(opexCashGiven),
    opexBankDebited: Math.round(opexBankDebited),
    cashGivenTotal: Math.round(cashGiven + opexCashGiven),
    bankDebitedTotal: Math.round(bankDebited + opexBankDebited),
  };
}

/** Live cash/bank = opening books (Accounts cutover) ± all movements since cutover. */
export function computeLiveCashBank(
  books: Pick<BooksOpeningState, 'cutoverDate' | 'balances'>,
  emiPayments: ReturnType<typeof readAdminEmiPayments> = readAdminEmiPayments()
): { cash: number; bank: number } {
  const openingCash = Number(books.balances.cash) || 0;
  const openingBank = Number(books.balances.bank) || 0;
  const cutover = books.cutoverDate ? toDate(books.cutoverDate) : null;
  const flowRange: DateRange = cutover ? { start: cutover, end: null } : { start: null, end: null };
  const flows = computePaymentFlows(flowRange, emiPayments);
  return {
    cash: Math.round(openingCash + flows.cashTaken - flows.cashGivenTotal),
    bank: Math.round(openingBank + flows.bankCredited - flows.bankDebitedTotal),
  };
}
